import { useState, useEffect, useMemo, useRef } from "react";
import { IoMdSettings } from "react-icons/io";
import { IoIosArrowDown } from "react-icons/io";
import logo from './../../assets/logos/logo.png';
import { MdEmail, MdLocalOffer, MdOutlinePriceCheck } from "react-icons/md";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import { GoSidebarExpand, GoSidebarCollapse } from "react-icons/go";
import {
  FaHeart,
  FaGift,
  FaShoppingCart,
  FaTruck,
  FaBoxes,
  FaFileInvoiceDollar,
  FaUsers,
  FaGamepad,
  FaChartBar,
  FaBullhorn,
  FaStore,
  FaClipboardList,
  FaChartLine,
} from "react-icons/fa";
import { useMenuAccess } from "../../contexts/MenuAccessContext";
import {
  GIFT_KARD_ENABLED_LOCATION_ID,
  SALES_LIMITED_LOCATION_ID,
  SEGMENTS_DISABLED_LOCATION_ID,
  WINE_SOL_LOCATION_ID,
} from "../../utils/routeAccess";

const menuItems = [
  {
    title: "Loyalty",
    icon: <FaHeart size={20} />,
    subItems: [
      { title: "Dashboard", path: "/loyalty/dashboard" },
      { title: "Reporting", path: "/loyalty/reporting" },
      { title: "Segments", path: "/loyalty/segments" },
      { title: "Program Settings", path: "/loyalty/program" },
    ],
  },
  {
    title: "Analytics",
    path: "/analytics",
    icon: <FaChartLine size={20} />,
  },
  {
    title: "Inventory",
    icon: <FaBoxes size={20} />,
    subItems: [
      { title: "Dashboard", path: "/inventory/dashboard" },
      { title: "Items", path: "/inventory/items" },
      { title: "Bulk Updates", path: "/inventory/bulk-updates" },
      { title: "Audit", path: "/inventory/audit" },
      // { title: "Reports", path: "/inventory/reports" },
    ],
  },
  {
    title: "Promotions",
    icon: <MdLocalOffer size={20} />,
    subItems: [
      { title: "Dashboard", path: "/promotions/dashboard" },
      { title: "View Promotions", path: "/promotions/coupons" },
    ],
  },
  {
    title: "Kards",
    icon: <FaGift size={20} />,
    subItems: [
      { title: "Dashboard", path: "/gift-kard/dashboard" },
      { title: "Kard Management", path: "/gift-kard/cards" },
      { title: "Reports", path: "/gift-kard/reports" },
      { title: "Settings", path: "/gift-kard/settings" },
    ],
  },
  {
    title: "Purchase",
    icon: <FaShoppingCart size={20} />,
    subItems: [
      { title: "Dashboard", path: "/purchase-management/dashboard" },
      { title: "My Vendors", path: "/purchase-management/vendors" },
      { title: "Invoices", path: "/purchase-management/invoices" },
      { title: "Purchase Orders", path: "/purchase-management/purchase-orders" },
      { title: "Product Catalog", path: "/purchase-management/product-catalogs" },
      { title: "Settings", path: "/purchase-management/settings" },
    ],
  },
  {
    title: "Wholesale",
    icon: <FaTruck size={20} />,
    subItems: [
      // { title: "Dashboard", path: "/whole-sales/dashboard" },
      { title: "My Retailers", path: "/whole-sales/retailers" },
      { title: "Invoices", path: "/whole-sales/invoices" },
      { title: "Purchase Orders", path: "/whole-sales/purchase-orders" },
      { title: "Product Catalog", path: "/whole-sales/product-catalogs" },
      // { title: "Reports", path: "/whole-sales/reports" },
      { title: "Settings", path: "/whole-sales/settings" },
    ],
    
  },
  {
    title: "Pricing",
    path: "/pricing",
    icon: <MdOutlinePriceCheck size={20} />,
  },
  {
    title: "Communication",
    icon: <MdEmail size={20} />,
    subItems: [
      { title: "Dashboard", path: "/communication/dashboard" },
      { title: "Analytics", path: "/communication/analytics" },
      { title: "Campaigns", path: "/communication/campaigns" },
      { title: "Notifications", path: "/communication/notifications" },
    ],
  },
  {
    title: "Sales",
    icon: <FaClipboardList size={20} />,
    subItems: [
      { title: "Dashboard", path: "/sales/dashboard" },
      { title: "Online Store", path: "/sales/online-store" },
      { title: "Orders", path: "/sales/orders" },
      // { title: "Reports", path: "/sales/reports" },
      { title: "Settings", path: "/sales/settings" },
    ],
  },
  {
    title: "Lottery",
    icon: <FaGamepad size={20} />,
    subItems: [
      { title: "Dashboard", path: "/lottery/dashboard" },
      { title: "Games", path: "/lottery/games" },
      { title: "Daily Closing", path: "/lottery/daily-closing" },
      // { title: "Receive Inventory", path: "/lottery/receive-inventory" },
      { title: "Audit", path: "/lottery/audit" },
      { title: "Reports", path: "/lottery/reports" },
      { title: "Settings", path: "/lottery/settings" },
    ],
  },
  {
    title: "Skan Data",
    icon: <FaChartBar size={20} />,
    subItems: [
      { title: "Dashboard", path: "/scan-data/dashboard" },
      { title: "Store Settings", path: "/scan-data/store-settings" },
      { title: "Reports", path: "/scan-data/reports" },
      { title: "RJR Discounts", path: "/scan-data/rjr-discounts" },
      { title: "Account", path: "/scan-data/accounts" },
      { title: "Help", path: "/scan-data/help" },
    ],
  },
  {
    title: "Account",
    path: "/account",
    icon: <IoMdSettings size={20} />,
  },
];

// ✅ Wine Sol Location ID
const HIDDEN_SALES_SUBITEMS = new Set(["Online Store", "Settings"]);
// ✅ Notifications requires the merchant to be registered for the mobile app,
// in addition to having the Communication module enabled.
const MOBILE_APP_ONLY_SUBITEMS = new Set(["Notifications"]);

// ✅ Wine Sol specific menu (separate modules)
const wineSolMenuItems = [
  // {
  //   title: "Dashboard",
  //   icon: <FaDatabase size={20} />,
  //   subItems: [
  //     { title: "A2A Dashboard", path: "/wine-sol/dashboard/a2a" },
  //   ],
  // },
  {
    title: "Marketing",
    icon: <FaBullhorn size={20} />,
    subItems: [
      { title: "Dashboard", path: "/wine-sol/marketing/dashboard" },
      { title: "Analytics", path: "/wine-sol/marketing/analytics" },

      { 
        title: "Website", 
        url: "https://mobile.mkonnekt.com/preview/5ed43d1b?t=1769178458701", 
        external: true 
      },
      { title: "Communication", path: "/wine-sol/marketing/communication/dashboard" },
    ],
  },
  {
    title: "Loyalty",
    icon: <FaHeart size={20} />,
    subItems: [
      { title: "Dashboard", path: "/loyalty/dashboard" },
      { title: "Reporting", path: "/loyalty/reporting" },
      { title: "Segments", path: "/loyalty/segments" },
      { title: "Program Settings", path: "/loyalty/program" },
    ],
  },
  {
    title: "Customer Management",
    icon: <FaUsers size={20} />,
    subItems: [
      { title: "Customer Management", path: "/wine-sol/customers" },
      { title: "Segments", path: "/wine-sol/segments" },
      { title: "Automations", path: "/wine-sol/automations" },
    ],
  },
  {
    title: "Coupons & Promotions",
    icon: <MdLocalOffer size={20} />,
    subItems: [
      { title: "Dashboard", path: "/promotions/dashboard" },
      { title: "View Promotions", path: "/promotions/coupons" },
    ],
  },
  {
    title: "Sales",
    icon: <FaStore size={20} />,
    subItems: [
      { 
        title: "Ecommerce", 
        url: "https://mobile.mkonnekt.com/preview/5ed43d1b?t=1769178458701", 
        external: true 
      },
      { title: "Dashboard", path: "/sales/dashboard" },
      { title: "Online Store", path: "/sales/online-store" },
      { title: "Reports", path: "/sales/reports" },
      { title: "Settings", path: "/sales/settings" },
    ],
  },
  // {
  //   title: "Sales",
  //   icon: <FaShoppingCart size={20} />,
  //   subItems: [
  //     { title: "Ecommerce", path: "/wine-sol/sales/ecommerce" },
  //     { title: "Clubs & Subscriptions", path: "/wine-sol/sales/clubs" },
  //   ],
  // },
];

const createPOMenuItems = [
  {
    title: "Purchase Orders",
    icon: <FaFileInvoiceDollar size={20} />,
    subItems: [
      { title: "Product Catalogs", path: "/product-catalogs/create-po" },
    ],
  },
];

const normalizePath = (path = "") => {
  const cleaned = path.split("?")[0];
  if (!cleaned || cleaned === "/") return "/";
  return cleaned.endsWith("/") ? cleaned.slice(0, -1) : cleaned;
};

const isModuleActive = (currentPath, menuPath) => {
  if (!menuPath) return false;
  const current = normalizePath(currentPath);
  const menu = normalizePath(menuPath);
  const withWildcard = `${menu}/*`;
  return !!matchPath({ path: withWildcard, end: false }, current);
};

const Sidebar = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  let { permissions, loading } = useMenuAccess();

  const queryParams = new URLSearchParams(search);
  const locationUid = queryParams.get("id");
  const editPoId = queryParams.get("edit-po-id");
  const isCreatePOFlow =
    queryParams.get("create-po") === "true" || pathname === "/product-catalogs/create-po";
  const isWholeSalesPoEditFlow =
    pathname.startsWith("/whole-sales/product-catalogs") && Boolean(editPoId);
  const isPurchasePoEditFlow =
    pathname.startsWith("/purchase-management/product-catalogs") && Boolean(editPoId);
  const isPoEditFlow = isWholeSalesPoEditFlow || isPurchasePoEditFlow;

  const isWineSolLocation = locationUid === WINE_SOL_LOCATION_ID;

  const baseMenuConfig = isWineSolLocation ? wineSolMenuItems: 
  isCreatePOFlow? createPOMenuItems
      : menuItems

  const activeMenuConfig = useMemo(() => {
    let config = baseMenuConfig;

    if (locationUid === SALES_LIMITED_LOCATION_ID) {
      config = config.map((item) => {
        if (item.title !== "Sales" || !item.subItems) return item;

        return {
          ...item,
          subItems: item.subItems.filter((subItem) => !HIDDEN_SALES_SUBITEMS.has(subItem.title)),
        };
      });
    }

    if (locationUid === SEGMENTS_DISABLED_LOCATION_ID) {
      config = config.map((item) => {
        if (item.title === "Loyalty" && item.subItems) {
          return {
            ...item,
            subItems: item.subItems.filter((subItem) => subItem.title !== "Segments"),
          };
        }
        if (item.title === "Communication" && item.subItems) {
          return {
            ...item,
            subItems: item.subItems.filter((subItem) => subItem.title !== "Notifications"),
          };
        }
        return item;
      });
    }

    if (!permissions?.mobileApp) {
      config = config.map((item) => {
        if (item.title !== "Communication" || !item.subItems) return item;

        return {
          ...item,
          subItems: item.subItems.filter((subItem) => !MOBILE_APP_ONLY_SUBITEMS.has(subItem.title)),
        };
      });
    }

    return config;
  }, [baseMenuConfig, locationUid, permissions]);

  const [expanded, setExpanded] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const poEditSidebarCollapsedRef = useRef(false);

  useEffect(() => {
    if (isPoEditFlow && !poEditSidebarCollapsedRef.current) {
      setExpanded(false);
      poEditSidebarCollapsedRef.current = true;
      return;
    }
    if (!isPoEditFlow) {
      poEditSidebarCollapsedRef.current = false;
    }
  }, [isPoEditFlow]);

  const hasMenuAccess = (title) => {
    if (isCreatePOFlow || isWineSolLocation) return true;

    switch (title) {
      case "Loyalty":
        return Boolean(permissions?.loyalty);
      case "Inventory":
        return Boolean(permissions?.inventory);
      case "Promotions":
        return Boolean(permissions?.coupons);
      case "Kards":
        return locationUid === GIFT_KARD_ENABLED_LOCATION_ID || Boolean(permissions?.giftCard);
      case "Purchase":
        return Boolean(permissions?.purchase);
      case "Wholesale":
        return Boolean(permissions?.wholesale);
      case "Communication":
        return Boolean(permissions?.communication);
      case "Pricing":
        return Boolean(permissions?.pricing);
      case "Skan Data":
        return Boolean(permissions?.scanData);
      case "Account":
        return Boolean(permissions?.settings);
      case "Sales":
        return Boolean(permissions?.sales);
      case "Lottery":
        return Boolean(permissions?.lottery);
      case "Analytics":
        return Boolean(permissions?.analyticsEngine);
      default:
        return false;
    }
  };

  useEffect(() => {
    if (!permissions) return;

    const filteredMenu = activeMenuConfig.filter((item) => hasMenuAccess(item.title));
    filteredMenu.forEach((item, idx) => {
      const hasActiveChild = item.subItems?.some(sub => {
        if (sub.path) {
          return isModuleActive(pathname, sub.path);
        }
        return false;
      });

      if (hasActiveChild) {
        setActiveMenu(idx);
      } else if (item.path && isModuleActive(pathname, item.path)) {
        setActiveMenu(idx);
      }
    });
  }, [pathname, permissions, loading, activeMenuConfig, isWineSolLocation, isCreatePOFlow]);

  if (loading) {
    return (
      <div
        className={`flex flex-col h-screen ${
          expanded ? "w-64" : "w-20"
        } bg-primaryBlue animate-pulse`}>
        <div className="p-4 bg-white"></div>
        <div className="flex-1 p-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-10 bg-blue-300/20 rounded mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  const filteredMenu = activeMenuConfig.filter((item) => hasMenuAccess(item.title));

  const withSidebarQuery = (path) => {
    const nextParams = new URLSearchParams();
    if (locationUid) {
      nextParams.set("id", locationUid);
    }
    if (isCreatePOFlow) {
      nextParams.set("create-po", "true");
    }
    const query = nextParams.toString();
    return query ? `${path}?${query}` : path;
  };

  const openMenuItem = (item, index, isParentActive) => {
    if (!item.subItems) {
      navigate(withSidebarQuery(item.path));
      return;
    }

    if (expanded) {
      setActiveMenu(isParentActive ? null : index);
      return;
    }

    setActiveMenu(index);
    const firstSubItem = item.subItems.find((subItem) => subItem.path || subItem.url);
    if (firstSubItem?.external && firstSubItem.url) {
      window.open(firstSubItem.url, "_blank");
    } else if (firstSubItem?.path) {
      navigate(withSidebarQuery(firstSubItem.path));
    }
  };


  return (
    <div
      className={`flex flex-col h-screen ${
        expanded ? "w-64" : "w-20"
      } bg-primaryBlue shadow-lg transition-all duration-300`}>
      
      <div className="flex justify-between items-center p-4 bg-white flex-shrink-0">
        {expanded && <img src={logo} className="h-16 w-auto" />}
        {expanded ? (
          <GoSidebarExpand
            size={28}
            onClick={() => setExpanded(false)}
            className="cursor-pointer text-primaryBlue"
          />
        ) : (
          <GoSidebarCollapse
            size={28}
            onClick={() => setExpanded(true)}
            className="cursor-pointer text-primaryBlue"
          />
        )}
      </div>

      <div className="flex-1 text-sm overflow-y-auto pt-10">
        <ul className="flex flex-col gap-2 px-1">

          {filteredMenu.map((item, index) => {
            const isParentActive = activeMenu === index;

            return (
              <li
                key={index}
                className={`rounded p-3 pl-4 ${
                  isParentActive
                    ? "border-[1px] text-primaryBlue bg-white shadow-md"
                    : "text-white shadow-md border-white border-[1px]"
                }`}
              >
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => openMenuItem(item, index, isParentActive)}
                >
                  <div className="flex items-center gap-3">
                    {item.icon}
                    {expanded && <h3 className="font-semibold truncate">{item.title}</h3>}
                  </div>

                  {expanded && item.subItems && (
                    <IoIosArrowDown
                      size={20}
                      className={`transition-transform ${isParentActive ? "rotate-180" : "rotate-0"}`}
                    />
                  )}
                </div>

                {expanded && isParentActive && item.subItems && (
                  <ul className="mx-3 py-3 flex flex-col gap-3">
                    {item.subItems.map((subItem, subIndex) => {
                      return (
                        <li
                          key={subIndex}
                          className={`p-2 pl-4 truncate hover:cursor-pointer ${
                            subItem.path && isModuleActive(pathname, subItem.path)
                              ? "text-white bg-primaryBlue"
                              : "bg-white text-primaryBlue"
                          }`}
                          onClick={() => {
                            if (subItem.external && subItem.url) {
                              window.open(subItem.url, "_blank");
                            } else if (subItem.path) {
                              navigate(withSidebarQuery(subItem.path));
                            }
                          }}
                        >
                          <h3 className="font-semibold truncate">{subItem.title}</h3>
                        </li>
                      );
                    })}
                  </ul>
                )}

              </li>
            );
          })}

        </ul>
      </div>
    </div>
  );
};

export default Sidebar;